import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Star, Plus, Zap } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function ProductCard({ product }) {
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);

  const variant = product.variants?.[0];
  const price = variant?.price ?? product.price_per_kg ?? 0;
  const weight = variant?.weight || variant?.label || 'per kg';
  const stock = Number(product.stock ?? 0);
  const discount = Number(product.discount_percentage || 0);
  const original = Number(product.original_price || 0);
  const rating = Number(product.avg_rating || 0);
  const reviews = Number(product.review_count || 0);
  const deliveryDays = product.delivery_days ?? product.expected_delivery_days;
  const img = product.images?.[0] || product.image || '/placeholder-product.jpg';

  const handleAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { navigate('/auth'); return; }
    if (!variant) { navigate(`/product/${product.product_id}`); return; }
    if (stock <= 0) return;
    setAdding(true);
    try {
      await addToCart(product.product_id, variant.name, 1);
      toast.success('Added to cart');
    } catch {
      toast.error('Could not add');
    } finally { setAdding(false); }
  };

  return (
    <Link
      to={`/product/${product.product_id}`}
      data-testid={`product-card-${product.product_id}`}
      className="group relative flex flex-col bg-card rounded-3xl overflow-hidden border border-border/50 shadow-sm hover:shadow-md transition-shadow w-full h-[360px]"
    >
      {/* IMAGE — strictly fixed height, never grows or shrinks */}
      <div className="relative w-full h-[200px] min-h-[200px] max-h-[200px] overflow-hidden bg-muted flex-shrink-0">
        <img
          src={img}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => { e.target.src = '/placeholder-product.jpg'; }}
        />

        {/* Discount badge — top left */}
        {discount > 0 && (
          <div
            data-testid={`discount-badge-${product.product_id}`}
            className="absolute top-3 left-3 bg-neutral-900 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full tracking-wide z-10"
          >
            {discount}% Off
          </div>
        )}

        {/* Special tag — top right */}
        {product.special_tag && (
          <div
            data-testid={`special-tag-${product.product_id}`}
            className="absolute top-3 right-3 bg-primary text-primary-foreground text-[11px] font-semibold px-2.5 py-1 rounded-full tracking-wide z-10"
          >
            {product.special_tag}
          </div>
        )}

        {/* Delivery chip — bottom left */}
        {deliveryDays === 0 && (
          <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 shadow z-10">
            <Zap className="h-2.5 w-2.5 text-primary" /> Same Day
          </div>
        )}
        {deliveryDays === 1 && (
          <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 shadow z-10">
            <Zap className="h-2.5 w-2.5 text-primary" /> Next Day
          </div>
        )}

        {/* Out of Stock overlay */}
        {stock <= 0 && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
            <span className="bg-white text-neutral-900 px-3 py-1.5 rounded-full text-xs font-semibold">
              Out of Stock
            </span>
          </div>
        )}
      </div>

      {/* CONTENT — takes exactly remaining 160px */}
      <div className="flex flex-col flex-1 px-4 pt-3 pb-4 overflow-hidden h-[160px] min-h-[160px] max-h-[160px]">

        {/* Product name — 1 line always */}
        <h3 className="font-semibold text-sm text-foreground leading-snug truncate w-full">
          {product.name}
        </h3>

        {/* Weight + rating row */}
        <div className="flex items-center gap-2 mt-2">
          <span className="inline-flex items-center bg-yellow-100 text-yellow-900 text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
            {weight}
          </span>
          {rating > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground flex-shrink-0">
              <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
              <span className="font-medium text-foreground">{rating.toFixed(1)}</span>
              {reviews > 0 && <span className="ml-0.5">({reviews})</span>}
            </span>
          )}
        </div>

        {/* Price + ADD — pinned to bottom */}
        <div className="flex items-center justify-between mt-auto">
          <div className="flex flex-col leading-tight">
            {original > price && (
              <span className="text-[11px] text-muted-foreground line-through">
                ₹{original}
              </span>
            )}
            <span className="text-base font-semibold leading-tight">₹{price}</span>
          </div>

          <button
            data-testid={`add-to-cart-${product.product_id}`}
            onClick={handleAdd}
            disabled={stock <= 0 || adding}
            className="inline-flex items-center gap-1 bg-primary text-primary-foreground text-xs font-semibold px-3.5 py-1.5 rounded-full hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0"
          >
            <Plus className="h-3 w-3" /> ADD
          </button>
        </div>
      </div>
    </Link>
  );
}
